import { useState, useRef, useEffect, useMemo } from 'react';
import useOutsideClick from '../../../../../hook/useOutsideClick';
import Portal from '../../../../../components/other/portal';
import './styles.scss';
import EditRoleModal from '../EditRoleModal';
import DeleteRoleModal from '../DeleteRoleModal';
import CellAssignmentSidebar from '../CellAssignmentSidebar';
import cx from 'classnames';
import { isDesktop, isMobile } from 'react-device-detect';
import DeleteRoleErrorModal from '../DeleteRoleErrorModal';
import { getRoleLabelsService } from '../../../../../services/company';
import { usePermission } from '../../../../../container/permissionContext';
import SchedulingCell from '../SchedulingCell';
import { getDateFromString } from '../../../../../utility';
import { useAuth } from '../../../../../container/authContext';

const SchedulingRoleCols = ({
  role,
  order,
  getPeriodData,
  handleUpdateUnitRole,
  unit,
  editSidebarSelectedUser,
  editSidebarAction,
  periodData,
  editSidebarFormData,
  editSidebarSelectedItems,
  setEditSidebarSelectedItems,
  shiftsData,
  setPeriodData,
  isPrint,
  roleLabels,
  canDelete,
  isPastPeriod,
  showRecurringShifts,
  setCellAssignmentData,
  cellAssignmentData,
  isSelectingPostShiftsActive,
  setSelectedShiftsToPost,
  selectedShiftsToPost,
}) => {
  const { user = {} } = useAuth();
  const buttonRef = useRef();
  const dropdownRef = useRef();
  const { hasPermissionByCodeName } = usePermission();
  const hasWritePermission = hasPermissionByCodeName('scheduling_management');

  const [dropdownPos, setDropdownPos] = useState(null);
  const [isManageRoleDropdownOpen, setIsManageRoleDropdownOpen] = useState(false);
  const [isEditRoleModalOpen, setIsEditRoleModalOpen] = useState(false);
  const [isDeleteRoleModalOpen, setIsDeleteRoleModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  useOutsideClick(dropdownRef, () => setIsManageRoleDropdownOpen(false));

  useEffect(() => {
    if (isManageRoleDropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isManageRoleDropdownOpen]);

  const name = useMemo(() => {
    if (role.name) {
      return role.name;
    } else if (role.role === 'cna') {
      return `${roleLabels?.cna_role_label}-${order}`;
    } else if (role.role === 'nurse') {
      return `${roleLabels?.nurse_role_label}-${order}`;
    }
  }, [role.name, role.role, order, roleLabels?.nurse_role_label, roleLabels?.cna_role_label]);

  const handleCellClick = (cell) => {
    if (editSidebarAction) {
      if (
        editSidebarAction === 'add' &&
        ((editSidebarSelectedUser.is_nurse && role.role === 'nurse') || (editSidebarSelectedUser.is_cna && role.role === 'cna')) &&
        !cell.both_halves_user &&
        !cell.is_both_halves_posted &&
        !cell.first_half_user &&
        !cell.is_first_half_posted &&
        !cell.second_half_user &&
        !cell.is_second_half_posted
      ) {
        if (editSidebarFormData.halfPart.value === 'both_halves') {
          setEditSidebarSelectedItems((prevState) => {
            if (prevState.find((item) => item.cell.id === cell.id && item.halfPart === 'both_halves')) {
              return prevState.filter((item) => !(item.cell.id === cell.id && item.halfPart === 'both_halves'));
            }

            // Validate if user has no no assignments in this shift
            if (
              periodData.units.find((dataUnit) =>
                dataUnit.roles.find((unitRole) =>
                  unitRole.cells.find(
                    (roleCell) =>
                      roleCell.day === cell.day &&
                      roleCell.shift_order === cell.shift_order &&
                      ((roleCell.first_half_user?.user && roleCell.first_half_user?.user.id === editSidebarSelectedUser.id) ||
                        (roleCell.both_halves_user?.user && roleCell.both_halves_user.user.id === editSidebarSelectedUser.id) ||
                        (roleCell.second_half_user?.user && roleCell.second_half_user.user.id === editSidebarSelectedUser.id)),
                  ),
                ),
              )
            ) {
              return prevState;
            }

            if (prevState.find((item) => item.cell.day === cell.day && item.cell.shift_order === cell.shift_order)) {
              return prevState;
            }

            return [
              ...prevState,
              { cell, halfPart: 'both_halves', shiftName: shiftsData[cell.shift_order], roleName: name, unitName: unit.unit.name },
            ];
          });
        } else if (
          editSidebarFormData.halfPart.value === 'first_half' &&
          !editSidebarSelectedItems.find((item) => item.cell.id === cell.id)
        ) {
          setEditSidebarSelectedItems((prevState) => {
            // Validate if user has no no assignments in this shift
            if (
              periodData.units.find((dataUnit) =>
                dataUnit.roles.find((unitRole) =>
                  unitRole.cells.find(
                    (roleCell) =>
                      roleCell.day === cell.day &&
                      roleCell.shift_order === cell.shift_order &&
                      ((roleCell.first_half_user?.user && roleCell.first_half_user?.user.id === editSidebarSelectedUser.id) ||
                        (roleCell.both_halves_user?.user && roleCell.both_halves_user.user.id === editSidebarSelectedUser.id)),
                  ),
                ),
              )
            ) {
              return prevState;
            }

            if (
              prevState.find(
                (item) =>
                  item.cell.day === cell.day &&
                  item.cell.shift_order === cell.shift_order &&
                  (item.halfPart === 'both_halves' || item.halfPart === 'first_half'),
              )
            ) {
              return prevState;
            }

            return [
              ...prevState,
              { cell, halfPart: 'first_half', shiftName: shiftsData[cell.shift_order], roleName: name, unitName: unit.unit.name },
            ];
          });
        } else if (
          editSidebarFormData.halfPart.value === 'second_half' &&
          !editSidebarSelectedItems.find((item) => item.cell.id === cell.id)
        ) {
          setEditSidebarSelectedItems((prevState) => {
            // Validate if user has no no assignments in this shift
            if (
              periodData.units.find((dataUnit) =>
                dataUnit.roles.find((unitRole) =>
                  unitRole.cells.find(
                    (roleCell) =>
                      roleCell.day === cell.day &&
                      roleCell.shift_order === cell.shift_order &&
                      ((roleCell.second_half_user?.user && roleCell.second_half_user?.user.id === editSidebarSelectedUser.id) ||
                        (roleCell.both_halves_user?.user && roleCell.both_halves_user.user.id === editSidebarSelectedUser.id)),
                  ),
                ),
              )
            ) {
              return prevState;
            }

            if (
              prevState.find(
                (item) =>
                  item.cell.day === cell.day &&
                  item.cell.shift_order === cell.shift_order &&
                  (item.halfPart === 'both_halves' || item.halfPart === 'second_half'),
              )
            ) {
              return prevState;
            }

            return [
              ...prevState,
              { cell, halfPart: 'second_half', shiftName: shiftsData[cell.shift_order], roleName: name, unitName: unit.unit.name },
            ];
          });
        }
      } else if (
        editSidebarAction === 'off_due_to' &&
        cell.both_halves_user?.user &&
        cell.both_halves_user.user.id === editSidebarSelectedUser.id
      ) {
        setEditSidebarSelectedItems((prevState) => {
          if (prevState.find((item) => item.cell.id === cell.id && item.halfPart === 'both_halves')) {
            return prevState.filter((item) => !(item.cell.id === cell.id && item.halfPart === 'both_halves'));
          }
          return [
            ...prevState,
            { cell, halfPart: 'both_halves', shiftName: shiftsData[cell.shift_order], roleName: name, unitName: unit.unit.name },
          ];
        });
      } else if (
        editSidebarAction === 'delete' &&
        cell.both_halves_user?.user &&
        cell.both_halves_user.user.id === editSidebarSelectedUser.id
      ) {
        setEditSidebarSelectedItems((prevState) => {
          if (prevState.find((item) => item.cell.id === cell.id && item.halfPart === 'both_halves')) {
            return prevState.filter((item) => !(item.cell.id === cell.id && item.halfPart === 'both_halves'));
          }
          return [
            ...prevState,
            { cell, halfPart: 'both_halves', shiftName: shiftsData[cell.shift_order], roleName: name, unitName: unit.unit.name },
          ];
        });
      }

      return;
    }

    if (isPrint || isMobile || !hasWritePermission || showRecurringShifts) {
      return;
    }

    if (
      !cell.first_half_user &&
      !cell.second_half_user &&
      !cell.both_halves_user &&
      !cell.is_first_half_posted &&
      !cell.is_second_half_posted &&
      !cell.is_both_halves_posted
    ) {
      if (isSelectingPostShiftsActive) {
        setSelectedShiftsToPost((prevState) => {
          if (prevState.find((item) => item.cell.id === cell.id)) {
            return prevState.filter((item) => item.cell.id !== cell.id);
          }
          return [
            ...prevState,
            { cell, halfPart: 'both_halves', shiftName: shiftsData[cell.shift_order], roleName: name, unitName: unit.unit.name },
          ];
        });
      } else {
        setCellAssignmentData({
          unit,
          cell,
          roleName: name,
          roleId: role.id,
          isPermanentRole: !!role.permanent_role,
          shiftLabel: shiftsData[cell.shift_order],
          available: 'both_halves',
          isPosted: cell.is_both_halves_posted,
          halfPart: 'both_halves',
          roleType: role.role,
          isPrint: isPrint,
        });
      }
    }
  };

  return (
    <>
      <td className="role-name-col">
        {isMobile || !hasWritePermission ? (
          <>{name}</>
        ) : (
          <button
            ref={buttonRef}
            onClick={(e) => setIsManageRoleDropdownOpen(true)}
            className="btn btn-outline role-dropdown-btn"
            disabled={isPrint}
          >
            {name}
            {!isPrint && (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M9.99984 10.832C10.4601 10.832 10.8332 10.4589 10.8332 9.9987C10.8332 9.53846 10.4601 9.16536 9.99984 9.16536C9.5396 9.16536 9.1665 9.53846 9.1665 9.9987C9.1665 10.4589 9.5396 10.832 9.99984 10.832Z"
                  fill="#667085"
                />
                <path
                  d="M9.99984 4.9987C10.4601 4.9987 10.8332 4.6256 10.8332 4.16536C10.8332 3.70513 10.4601 3.33203 9.99984 3.33203C9.5396 3.33203 9.1665 3.70513 9.1665 4.16536C9.1665 4.6256 9.5396 4.9987 9.99984 4.9987Z"
                  fill="#667085"
                />
                <path
                  d="M9.99984 16.6654C10.4601 16.6654 10.8332 16.2923 10.8332 15.832C10.8332 15.3718 10.4601 14.9987 9.99984 14.9987C9.5396 14.9987 9.1665 15.3718 9.1665 15.832C9.1665 16.2923 9.5396 16.6654 9.99984 16.6654Z"
                  fill="#667085"
                />
                <path
                  d="M9.99984 10.832C10.4601 10.832 10.8332 10.4589 10.8332 9.9987C10.8332 9.53846 10.4601 9.16536 9.99984 9.16536C9.5396 9.16536 9.1665 9.53846 9.1665 9.9987C9.1665 10.4589 9.5396 10.832 9.99984 10.832Z"
                  stroke="#667085"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.99984 4.9987C10.4601 4.9987 10.8332 4.6256 10.8332 4.16536C10.8332 3.70513 10.4601 3.33203 9.99984 3.33203C9.5396 3.33203 9.1665 3.70513 9.1665 4.16536C9.1665 4.6256 9.5396 4.9987 9.99984 4.9987Z"
                  stroke="#667085"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.99984 16.6654C10.4601 16.6654 10.8332 16.2923 10.8332 15.832C10.8332 15.3718 10.4601 14.9987 9.99984 14.9987C9.5396 14.9987 9.1665 15.3718 9.1665 15.832C9.1665 16.2923 9.5396 16.6654 9.99984 16.6654Z"
                  stroke="#667085"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        )}
      </td>
      {role.cells
        .slice()
        .sort((a, b) => a.day.localeCompare(b.day))
        .map((cell, index) => (
          <td
            key={index}
            onClick={() => handleCellClick(cell)}
            className={cx('shift-assignment-col', {
              filled: cell.assignments?.length > 0,
              isDesktop,
              separated: cell.is_first_half_posted || cell.is_second_half_posted || cell.first_half_user || cell.second_half_user,
              'first-in-date': index % 3 === 0,
              'has-red-border': index > 2 && getDateFromString(cell.day).getDay() === 0 && index % 3 === 0,
              isActive:
                (cellAssignmentData?.cell?.id === cell.id && cellAssignmentData?.halfPart === 'both_halves') ||
                selectedShiftsToPost?.find((item) => item.cell.id === cell.id && item.halfPart === 'both_halves'),
              isSelectedToAdd:
                editSidebarAction === 'add' &&
                editSidebarSelectedItems?.find((item) => item.cell.id === cell.id && item.halfPart === 'both_halves'),
            })}
          >
            <SchedulingCell
              periodData={periodData}
              editSidebarSelectedItems={editSidebarSelectedItems}
              editSidebarAction={editSidebarAction}
              editSidebarFormData={editSidebarFormData}
              setEditSidebarSelectedItems={setEditSidebarSelectedItems}
              editSidebarSelectedUser={editSidebarSelectedUser}
              cell={cell}
              getPeriodData={getPeriodData}
              isPrint={isPrint}
              role={role}
              unit={unit}
              shiftsData={shiftsData}
              setCellAssignmentData={setCellAssignmentData}
              cellAssignmentData={cellAssignmentData}
              isPastPeriod={isPastPeriod}
              name={name}
              showRecurringShifts={showRecurringShifts}
              isSelectingPostShiftsActive={isSelectingPostShiftsActive}
              setSelectedShiftsToPost={setSelectedShiftsToPost}
              selectedShiftsToPost={selectedShiftsToPost}
            />
          </td>
        ))}
      {isManageRoleDropdownOpen && dropdownPos && !isPrint && hasWritePermission && (
        <Portal>
          <ul
            ref={dropdownRef}
            className="role-dropdown-menu"
            style={{
              position: 'absolute',
              top: `${dropdownPos.top}px`,
              left: `${dropdownPos.left}px`,
              zIndex: 9999,
            }}
          >
            <li>
              <button
                className="btn btn-left btn-full btn-text"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsManageRoleDropdownOpen(false);
                  setIsEditRoleModalOpen(true);
                }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_13800_73972)">
                    <path
                      d="M9.16675 3.33027H3.33341C2.89139 3.33027 2.46746 3.50586 2.1549 3.81842C1.84234 4.13098 1.66675 4.55491 1.66675 4.99693V16.6636C1.66675 17.1056 1.84234 17.5296 2.1549 17.8421C2.46746 18.1547 2.89139 18.3303 3.33341 18.3303H15.0001C15.4421 18.3303 15.866 18.1547 16.1786 17.8421C16.4912 17.5296 16.6667 17.1056 16.6667 16.6636V10.8303M15.4167 2.08027C15.7483 1.74875 16.1979 1.5625 16.6667 1.5625C17.1356 1.5625 17.5852 1.74875 17.9167 2.08027C18.2483 2.41179 18.4345 2.86143 18.4345 3.33027C18.4345 3.79911 18.2483 4.24875 17.9167 4.58027L10.0001 12.4969L6.66675 13.3303L7.50008 9.99693L15.4167 2.08027Z"
                      stroke="#101828"
                      strokeWidth="1.67"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_13800_73972">
                      <rect width="20" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                Edit line
              </button>
            </li>
            {canDelete && (
              <li>
                <button
                  className="btn btn-left btn-full btn-text delete-role-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsManageRoleDropdownOpen(false);
                    setIsDeleteRoleModalOpen(true);
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M2.5 4.9974H4.16667M4.16667 4.9974H17.5M4.16667 4.9974V16.6641C4.16667 17.1061 4.34226 17.53 4.65482 17.8426C4.96738 18.1551 5.39131 18.3307 5.83333 18.3307H14.1667C14.6087 18.3307 15.0326 18.1551 15.3452 17.8426C15.6577 17.53 15.8333 17.1061 15.8333 16.6641V4.9974H4.16667ZM6.66667 4.9974V3.33073C6.66667 2.8887 6.84226 2.46478 7.15482 2.15222C7.46738 1.83966 7.89131 1.66406 8.33333 1.66406H11.6667C12.1087 1.66406 12.5326 1.83966 12.8452 2.15222C13.1577 2.46478 13.3333 2.8887 13.3333 3.33073V4.9974M8.33333 9.16406V14.1641M11.6667 9.16406V14.1641"
                      stroke="#B42318"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Delete line
                </button>
              </li>
            )}
          </ul>
        </Portal>
      )}
      {isEditRoleModalOpen && !isPrint && (
        <EditRoleModal
          closeModal={() => setIsEditRoleModalOpen(false)}
          roleName={name}
          roleId={role.id}
          handleUpdateUnitRole={handleUpdateUnitRole}
        />
      )}
      {isDeleteRoleModalOpen && !isPrint && (
        <DeleteRoleModal
          closeModal={() => setIsDeleteRoleModalOpen(false)}
          roleId={role.id}
          setPeriodData={setPeriodData}
          unitId={unit.id}
          setIsErrorModalOpen={setIsErrorModalOpen}
        />
      )}
      {isErrorModalOpen && <DeleteRoleErrorModal closeModal={() => setIsErrorModalOpen(false)} />}
    </>
  );
};

export default SchedulingRoleCols;
